import { select } from '@ps/web-core-store';
import { useTrackers } from '@ps/web-shared-analytics';
import { Loader } from '@ps/web-shared-ui';
import classNames from 'clsx';
import { camelCase } from 'lodash';
import type { ButtonHTMLAttributes, DOMAttributes, ReactNode } from 'react';
import type React from 'react';
import { useRef } from 'react';
import { useSelector } from 'react-redux';

import type { LinkProps } from '../../Link/Link';
import Link from '../../Link/Link';
import styles from '../StarsButton.module.scss';
import type { ButtonSize, ButtonVariant } from '../constants';

export type Props = LinkProps &
  Partial<DOMAttributes<HTMLButtonElement> & ButtonHTMLAttributes<HTMLButtonElement>> & {
    block?: boolean;
    disabled?: boolean;
    icon?: boolean;
    inverse?: boolean;
    loading?: boolean;
    size?: ButtonSize;
    title?: ReactNode | Element;
    variant?: ButtonVariant;
    routeValues?: { [key: string]: string };
  };

const getClassName = (props: Props) =>
  classNames(
    styles.button,
    styles[camelCase(props.size) as keyof typeof styles],
    styles[camelCase(props.variant) as keyof typeof styles],
    {
      [styles.block]: props.block,
      [styles.disabled]: typeof props.disabled === 'boolean' ? props.disabled : props.loading,
      [styles.loading]: props.loading,
      [styles.inverse]: props.inverse,
      [styles.icon]: props.icon
    },
    props.className
  );

const RawButton: React.FC<Props> = (props) => {
  const locale = useSelector(select.locale.get);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { buttonPressed } = useTrackers();

  const {
    block,
    loading,
    href,
    icon,
    alias,
    size = 'large',
    disabled,
    variant = 'primary',
    type = 'button',
    inverse,
    activeClassName,
    isActive,
    routeValues,
    track,
    trackText,
    ...rest
  } = props;
  const className = getClassName({ ...props, size, variant });
  const children = loading ? <Loader /> : rest.children;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { onClick } = props;
    const trackData = trackText || (locale === 'en' ? buttonRef.current?.innerText : undefined);

    onClick && onClick(event);
    track && buttonPressed({ buttonName: trackData });
  };

  if (href || alias) {
    return (
      <Link
        {...rest}
        href={href}
        values={routeValues}
        alias={alias}
        className={className}
        activeClassName={activeClassName}
        isActive={isActive}
        track={track || false}
        trackText={trackText}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      ref={buttonRef}
      {...rest}
      type={type}
      disabled={disabled || loading}
      className={className}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

export default RawButton;
